"use client";

import type { CSSProperties, ReactNode } from "react";

import {
  ACCENT_TEXT_COLOR_CLASS,
  ACCENT_TEXT_COLOR_HEX,
  isAccentTextFillAnimation,
  resolveAccentTextColor,
} from "@homeinstead/schemas";
import { createContext, isValidElement, useContext } from "react";
import { cn } from "../../utils/classnames";
import {
  ACCENT_TEXT_FILL_STAGGER_MILLISECONDS,
  ACCENT_TEXT_FILL_START_MILLISECONDS,
} from "./accent-text-fill-timing";

/**
 * Portable Text marks do not receive the parent block style. Headings set
 * this so accent-fill only letter-animates in headings; elsewhere it stays
 * a solid accent color, matching the Studio warning.
 */
const AccentHeadingContext = createContext(false);

function AccentHeading({ children }: { children?: ReactNode }) {
  return (
    <AccentHeadingContext.Provider value={true}>
      {children}
    </AccentHeadingContext.Provider>
  );
}

type AccentTextProps = {
  children?: ReactNode;
  value?: {
    animation?: string;
    color?: string;
  };
};

type AccentLetter = {
  character: string;
  key: string;
};

/**
 * CSSType has no index signature for custom properties. The cast stays at this
 * boundary so each letter can set `--accent-index` without a timer.
 */
function accentStyle(properties: {
  [key: `--${string}`]: number | string;
  animationDelay?: string;
}): CSSProperties {
  return properties as CSSProperties;
}

function getTextContent(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map((child) => getTextContent(child)).join("");
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return getTextContent(node.props.children);
  }

  return "";
}

function containsElement(node: ReactNode): boolean {
  if (Array.isArray(node)) {
    return node.some((child) => containsElement(child));
  }

  return isValidElement(node);
}

function buildAccentLetters(text: string): AccentLetter[] {
  const seen = new Map<string, number>();

  return Array.from(text).map((character) => {
    const occurrence = seen.get(character) ?? 0;
    seen.set(character, occurrence + 1);

    return {
      character,
      key: `${character}-${occurrence}`,
    };
  });
}

/**
 * Letter spans are hidden from the accessibility tree and the phrase is
 * repeated once for assistive tech, so a heading's name stays the plain text.
 * `animation-fill-mode: both` holds the inherited heading color during the
 * delay and the accent color afterwards. The wrapper omits the accent color
 * class so letters can inherit the heading before they fill. The animation
 * utility is `sm:` and up; smaller viewports and `motion-reduce` stay solid
 * accent via the utilities below. The delay is an inline style so it wins
 * over the animation shorthand, which would otherwise reset it to zero.
 */
function AccentText({ children, value }: AccentTextProps) {
  const color = resolveAccentTextColor(value?.color);
  const colorClass = ACCENT_TEXT_COLOR_CLASS[color];
  const inHeading = useContext(AccentHeadingContext);

  // Nested marks stay intact. Splitting them into letters would drop the
  // inner link, text size, or decorator.
  if (
    !isAccentTextFillAnimation(value?.animation) ||
    !inHeading ||
    containsElement(children)
  ) {
    return <span className={colorClass}>{children}</span>;
  }

  const text = getTextContent(children);
  const letters = buildAccentLetters(text);

  if (letters.length === 0) {
    return <span className={colorClass}>{children}</span>;
  }

  return (
    <span
      style={accentStyle({ "--accent-color": ACCENT_TEXT_COLOR_HEX[color] })}
    >
      <span className="sr-only">{text}</span>
      {letters.map((letter, index) => (
        <span
          aria-hidden="true"
          className={cn(
            "sm:animate-accent-fill",
            "max-sm:text-[color:var(--accent-color)] max-sm:[-webkit-text-fill-color:var(--accent-color)]",
            "motion-reduce:!animate-none motion-reduce:text-[color:var(--accent-color)] motion-reduce:[-webkit-text-fill-color:var(--accent-color)]",
          )}
          key={letter.key}
          style={accentStyle({
            "--accent-index": index,
            animationDelay: `calc(${ACCENT_TEXT_FILL_START_MILLISECONDS}ms + var(--accent-index) * ${ACCENT_TEXT_FILL_STAGGER_MILLISECONDS}ms)`,
          })}
        >
          {letter.character}
        </span>
      ))}
    </span>
  );
}

export { AccentHeading, AccentText };
